<script setup lang="ts">
  import { defaultMessageDurationSeconds } from '@/utils'
  import { Button, message } from 'ant-design-vue'
  import {
    CopyOutlined,
    DownloadOutlined,
    EditOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    UploadOutlined
  } from '@ant-design/icons-vue'
  import { nextTick, onMounted, onUnmounted, reactive, ref, watchEffect } from 'vue'
  import { useDark } from '@vueuse/core'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import JSONEditor from 'jsoneditor'
  import { debounceTime, Subject } from 'rxjs'
  import { PlotSchema } from '../models/PlotSchema'
  import { Plot } from '../models/Plot'
  import { PlottingService } from '../services/PlottingService'
  import { PlottingJsonParsingService } from '../services/PlottingJsonParsingService'

  interface IPlotJsonManagementProps {
    plotSchemas: Array<PlotSchema>
    plots: Array<Plot>
    visibilityChanged: boolean
    parentExecutionId: string
    executionName: string
    plottingJsonParsingService: PlottingJsonParsingService
  }

  interface IPlotJsonManagementEmits {
    (e: 'plots-applied'): void
    (e: 'update:executionName', newValue: string): void
  }

  const props = defineProps<IPlotJsonManagementProps>()
  const emit = defineEmits<IPlotJsonManagementEmits>()

  const nonAppliedPlots = reactive<Array<Plot>>([...props.plots])
  const activePlots = reactive<Array<Plot>>(props.plots)
  const nonAppliedExecutionName = ref<string>(props.executionName)

  const activeView = ref<'view' | 'edit'>('view')
  const editorHasChanges = ref<boolean>(false)

  const formattedPlotsJson = ref<any>()
  const dependencyChange$ = new Subject<void>()

  dependencyChange$.pipe(debounceTime(300)).subscribe(() => {
    formattedPlotsJson.value = PlottingService.getFormattedPlotsJson(
      props.parentExecutionId,
      nonAppliedExecutionName.value,
      activePlots
    )
  })

  watchEffect(() => {
    props.parentExecutionId
    nonAppliedExecutionName.value,
    activePlots
    dependencyChange$.next()
  })

  const importedJson = ref<string | undefined>()
  const isDarkMode = useDark()
  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const toggleViewMode = () => {
    activeView.value = activeView.value === 'view' ? 'edit' : 'view'

    nextTick(() => {
      if (activeView.value === 'edit') {
        initializeJSONEditor()
      } else {
        destroyEditor()
      }
    })
  }

  const destroyEditor = () => {
    if (editor) {
      editor.destroy()
    }
  }

  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(
        PlottingService.getFormattedPlotsJsonString(
          props.parentExecutionId,
          nonAppliedExecutionName.value,
          activePlots
        )
      )
      message.success('JSON copied to clipboard!', defaultMessageDurationSeconds)
    } catch (error) {
      message.error('Failed to copy to clipboard', defaultMessageDurationSeconds)
    }
  }

  // Ref for the hidden file input
  const fileInput = ref<HTMLInputElement | null>(null)

  const downloadJsonFile = () => {
    const jsonContent = PlottingService.getFormattedPlotsJsonString(
      props.parentExecutionId,
      nonAppliedExecutionName.value,
      activePlots
    )
    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `plots-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    message.success('JSON file downloaded!', defaultMessageDurationSeconds)
  }

  const importJsonFile = () => {
    // Reset JSON Editor
    nonAppliedPlots.splice(0, nonAppliedPlots.length)
    nonAppliedExecutionName.value = ''
    setJSONEditorModel(props.parentExecutionId, nonAppliedExecutionName.value, nonAppliedPlots)
    editorHasChanges.value = true
    fileInput.value?.click()
  }

  const handleFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (file) {
      // Optional: validate file type
      if (!file.name.endsWith('.json')) {
        message.error('Please select a valid .json file', defaultMessageDurationSeconds)
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        importedJson.value = reader.result as string
        const result = updateNonAppliedPlotsFromJsonString(importedJson.value)
        if (result === true)
          message.success('JSON parsed successfully.', defaultMessageDurationSeconds)
      }
      reader.readAsText(file)
    }

    // Reset input value so the same file can be selected again later
    input.value = ''
  }

  const updateNonAppliedPlotsFromJsonString = (json: string): boolean => {
    if (json == null) throw new Error('valid json required')
    const result = props.plottingJsonParsingService.tryParseEntitiesFromJson(
      json,
      props.plotSchemas
    )
    if (!result.valid) {
      message.error(
        `Error parsing JSON: ${result.errors?.join(', ')}`,
        defaultMessageDurationSeconds
      )
      return false
    } else {
      // Passes validation should expect rules to exist
      nonAppliedPlots.splice(0, nonAppliedPlots.length, ...(result.parsedEntities as Array<Plot>))
      nonAppliedExecutionName.value = result.name ?? ''
      setJSONEditorModel(props.parentExecutionId, nonAppliedExecutionName.value, nonAppliedPlots)
      return true
    }
  }

  const applyJsonPlots = () => {
    try {
      const updatedPayloadString = editor?.getText()
      const result = updateNonAppliedPlotsFromJsonString(updatedPayloadString)
      if (result === true) {
        message.success('JSON parsed and applied successfully.', defaultMessageDurationSeconds)
        activePlots.splice(0, activePlots.length, ...nonAppliedPlots)
        emit('update:executionName', nonAppliedExecutionName.value)
        editorHasChanges.value = false
      }
      emit('plots-applied')
    } catch (e) {
      message.error(`Error getting payload value from JSONEditor`, defaultMessageDurationSeconds)
    }
  }

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editorHasChanges.value = false
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          editorHasChanges.value = true
        }
      })
      setJSONEditorModel(props.parentExecutionId, nonAppliedExecutionName.value, activePlots)
    }
  }

  const setJSONEditorModel = (
    parentExecutionId: string,
    executionName: string,
    plots: Array<Plot>
  ) => {
    if (editor != null && editorContainer.value != null) {
      const json = PlottingService.getFormattedPlotsJson(parentExecutionId, executionName, plots)
      editor.set(json)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  onMounted(() => {
    initializeJSONEditor()
    formattedPlotsJson.value = PlottingService.getFormattedPlotsJson(
      props.parentExecutionId,
      nonAppliedExecutionName.value,
      activePlots
    )
  })

  onUnmounted(() => {
    destroyEditor()
  })
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Hidden file input -->
    <input type="file" ref="fileInput" accept=".json" class="hidden" @change="handleFileSelected" />

    <!-- Header Actions -->
    <div class="mb-4 flex justify-end gap-2">
      <a-tooltip placement="bottom" v-if="activeView === 'view'">
        <template #title>{{ 'Edit or Import JSON' }}</template>
        <Button class="flex items-center" type="primary" @click="toggleViewMode">
          <EditOutlined />
          {{ 'Edit JSON' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom" v-else-if="activeView === 'edit'">
        <template #title>
          {{ editorHasChanges === true ? 'All changes will be lost.' : 'Select to view JSON.' }}
        </template>
        <Button
          :class="['flex items-center', { warning: editorHasChanges === true }]"
          @click="toggleViewMode"
        >
          <SearchOutlined />
          {{ 'View Only' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Copy Applied JSON to clipboard</template>
        <Button class="flex items-center" @click="copyJsonToClipboard">
          <CopyOutlined />
          Copy to Clipboard
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Download Applied JSON</template>
        <Button class="flex items-center" @click="downloadJsonFile">
          <DownloadOutlined />
          Download JSON
        </Button>
      </a-tooltip>

      <Button class="flex items-center" :disabled="activeView !== 'edit'" @click="importJsonFile">
        <UploadOutlined />
        Import JSON
      </Button>
      <Button
        type="primary"
        class="flex items-center"
        :disabled="activeView === 'view' || editorHasChanges === false"
        @click="applyJsonPlots"
      >
        <PlayCircleOutlined />
        Apply
      </Button>
    </div>

    <!-- JSON Content -->
    <div class="flex-1">
      <div class="relative z-[10] flex"></div>
      <div class="z-[1] mt-[10px]">
        <!-- View -->
        <div
          v-if="activeView === 'view'"
          class="overflow-auto rounded-sm border bg-card p-1"
          style="max-height: calc(100vh - 300px)"
        >
          <div :class="['json-pretty-wrapper', 'viewer-container', { 'dark-mode': isDarkMode }]">
            <vue-json-pretty :data="formattedPlotsJson as any" />
          </div>
        </div>
        <!-- Edit -->
        <div class="editor-container" v-else-if="activeView === 'edit'" ref="editorContainer"></div>
      </div>
    </div>
  </div>
</template>

<style>
  .editor-container {
    height: calc(100vh - 165px);
  }
  .viewer-container {
    min-height: calc(100vh - 165px);
    height: 100%;
  }
</style>
